import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { observerApi } from "../services/observerApi";
import { Assertion, PublicProfile, Review } from "../types/observer";
import SeoHead from "../components/SeoHead";
import { categoryForTag } from "../data/practiceCategories";
import { REVIEW_DIMENSIONS } from "../data/reviewDimensions";
import { FactStrip, ProfileAvatar, ShareActions } from "../components/ObserverProfileBits";
import SaveToLibraryButton from "../components/SaveToLibraryButton";

const COPY = {
  unclaimed:
    "Compiled by Observer Theory from public sources. This person has not verified or managed this profile. Details may be out of date — you can suggest a correction.",
  claimed:
    "Claimed and identity-verified. Verification confirms this person controls the linked channels. It is not an endorsement or an assessment of quality.",
  zero: "No verified Observer Theory reviews yet. We don't estimate ratings or import them from elsewhere.",
};
const date = (v: string) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(v));
const typeLabel = (a: Assertion) =>
  ({
    observed_fact: "Observed fact",
    self_described: "Provided by the practitioner. Not independently verified.",
    platform_provided: "Platform-provided",
    editorial_summary: "Editorial summary",
    community_reported: "Community-reported",
    unknown: "Not publicly available.",
  })[a.assertionType];
const ReviewCard = ({ r, entityId }: { r: Review; entityId: string }) => (
  <article className="ot-review">
    <div>
      <b>
        {r.class === "verified_purchase"
          ? "Verified purchase — evidence checked by Observer Theory."
          : r.class === "unverified_client"
            ? "Client — purchase not verified."
            : "Content opinion"}
      </b>
      {r.conflictDeclared && (
        <em>This reviewer says they offer competing services.</em>
      )}
    </div>
    {r.underDispute && <mark>Under review</mark>}
    <p>{r.body}</p>
    <small>
      {r.displayName || "Pseudonymous reviewer"} · experience{" "}
      {date(r.experienceDate)}
    </small>
    {r.reply && (
      <aside>
        <b>Practitioner response</b>
        <p>{r.reply.body}</p>
      </aside>
    )}
    <Link to={`/report?kind=reply&review=${r.id}&entity=${entityId}`}>
      Reply — free
    </Link>{" "}
    ·{" "}
    <Link to={`/report?kind=dispute&review=${r.id}&entity=${entityId}`}>
      Dispute this review — free
    </Link>
  </article>
);

export default function ObserverProfilePage() {
  const { id = "" } = useParams(),
    [searchParams] = useSearchParams(),
    [p, setP] = useState<PublicProfile>(),
    [error, setError] = useState("");
  useEffect(() => {
    const attribution = searchParams.get("via");
    if (attribution) localStorage.setItem("observer_share_attribution", attribution);
    observerApi
      .profile(id)
      .then((x) => {
        setP(x);
        observerApi.event("profile_view");
        observerApi.event("record_panel_view");
        observerApi.event("transparency_panel_view");
        observerApi.event("transparency_explainer_view");
        (
          ["verified_purchase", "unverified_client", "content_opinion"] as const
        ).forEach((cls) =>
          observerApi.event("review_panel_view", undefined, { class: cls }),
        );
        x.offers.forEach(() => observerApi.event("offer_card_view"));
      })
      .catch(() =>
        setError("This profile is unavailable or is not published."),
      );
  }, [id, searchParams]);
  useEffect(() => {
    if (!p || !window.location.hash) return;
    // Profile data arrives asynchronously. Browsers attempt fragment
    // navigation before these sections exist, so explicitly honor direct
    // links once the requested panel has rendered.
    const target = document.getElementById(
      decodeURIComponent(window.location.hash.slice(1)),
    );
    target?.scrollIntoView({ block: "start" });
  }, [p]);
  const grouped = useMemo(() => {
    const m: Record<string, Assertion[]> = {};
    p?.assertions
      .filter((a) => a.field !== "transparency_response")
      .forEach((a) => (m[a.field] ??= []).push(a));
    return m;
  }, [p]);
  const profileSEO = useMemo(() => {
    if (!p) return undefined;
    const practices = p.entity.practiceTags.join(", ");
    const title = `${p.entity.displayName}: ${practices || "Spiritual Practitioner"} | Observer Theory`;
    const description = `Research ${p.entity.displayName}${practices ? `, including ${practices}` : ""}: teachings, official channels, public work, sourced facts, provenance, and evidence-classed reviews.`;
    const primaryCategory = p.entity.practiceTags
      .map(categoryForTag)
      .find(Boolean);
    const profileURL = `https://observertheory.com/profile/${p.entity.publicId}`;
    const breadcrumbItems: Record<string, unknown>[] = [
      {
        "@type": "ListItem",
        position: 1,
        name: "Practitioners",
        item: "https://observertheory.com/practitioners",
      },
    ];
    if (primaryCategory) {
      breadcrumbItems.push({
        "@type": "ListItem",
        position: 2,
        name: primaryCategory.shortName,
        item: `https://observertheory.com/practitioners/${primaryCategory.slug}`,
      });
    }
    breadcrumbItems.push({
      "@type": "ListItem",
      position: breadcrumbItems.length + 1,
      name: p.entity.displayName,
      item: profileURL,
    });
    return {
      title,
      description,
      primaryCategory,
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            name: title,
            description,
            url: profileURL,
            dateModified: p.entity.updatedAt,
            about: {
              "@type":
                p.entity.entityType === "individual" ||
                p.entity.entityType === "person"
                  ? "Person"
                  : "Organization",
              name: p.entity.displayName,
              alternateName: [
                ...(p.entity.aliases || []),
                ...(p.entity.formerNames || []),
              ],
              sameAs: p.platformAccounts.map((account) => account.url),
            },
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbItems,
          },
          ...p.reviews.map((review) => ({
            "@type": "Review",
            author: { "@type": "Person", name: review.displayName || "Pseudonymous reviewer" },
            datePublished: review.publishedAt,
            reviewBody: review.body,
            itemReviewed: {
              "@type": p.entity.entityType === "individual" || p.entity.entityType === "person" ? "Person" : "Organization",
              name: p.entity.displayName,
              url: profileURL,
            },
          })),
        ],
      },
    };
  }, [p]);
  if (error)
    return (
      <div className="ot-page">
        <SeoHead
          title="Profile unavailable | Observer Theory"
          description="This practitioner profile is not currently available."
          path={`/profile/${id}`}
          noIndex
        />
        <div className="ot-empty">
          <h1>Record unavailable</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  if (!p) return <div className="ot-page">Loading the record…</div>;
  const verified = p.reviews.filter((r) => r.class === "verified_purchase"),
    unverified = p.reviews.filter((r) => r.class === "unverified_client"),
    opinions = p.reviews.filter((r) => r.class === "content_opinion");
  return (
    <div className="ot-page ot-profile">
      {profileSEO && (
        <SeoHead
          title={profileSEO.title}
          description={profileSEO.description}
          path={`/profile/${p.entity.publicId}`}
          jsonLd={profileSEO.jsonLd}
          noIndex={!p.entity.indexable || p.assertions.some((assertion) => assertion.disputed)}
        />
      )}
      <nav className="ot-breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true">/</span>
        <Link to="/practitioners">Practitioners</Link>
        {profileSEO?.primaryCategory && (
          <>
            <span aria-hidden="true">/</span>
            <Link
              to={`/practitioners/${profileSEO.primaryCategory.slug}`}
            >
              {profileSEO.primaryCategory.shortName}
            </Link>
          </>
        )}
        <span aria-hidden="true">/</span>
        <span>{p.entity.displayName}</span>
      </nav>
      <header className="ot-profile-head">
        <ProfileAvatar entity={p.entity} />
        <div className="ot-profile-categories">
          <span>{p.entity.entityType}</span>
          {p.entity.practiceTags.map((tag) => {
            const category = categoryForTag(tag);
            return category ? (
              <Link
                key={tag}
                to={`/practitioners/${category.slug}`}
              >
                {tag}
              </Link>
            ) : (
              <span key={tag}>{tag}</span>
            );
          })}
        </div>
        <h1>{p.entity.displayName}</h1>
        <FactStrip entity={p.entity} />
        <b>
          {p.entity.claimState === "claimed" ? "Claimed" : "Unclaimed profile"}
        </b>
        {p.entity.identityVerified && <b>Identity verified</b>}
        <p>{p.entity.identityVerified ? COPY.claimed : COPY.unclaimed}</p>
        {p.entity.claimState === "claimed" && (
          <small>
            Claimed — this practitioner invites reviews through Observer Theory.
          </small>
        )}
        <SaveToLibraryButton entityId={p.entity.id} />
        <div>
          {p.platformAccounts.map((a) => (
            <span key={a.id}>
              <a
                href={a.url}
                rel="noreferrer"
                onClick={() => observerApi.event("source_click")}
              >
                {a.platform}: {a.handle}
              </a>{" "}
              <Link
                to={`/report?kind=impersonation&entity=${p.entity.id}&platformAccount=${a.id}`}
              >
                Report this account
              </Link>
            </span>
          ))}
          {p.entity.claimState !== "claimed" && (
            <div className="ot-claim-first"><Link className="button button-ink" to={"/claim?entity=" + p.entity.id}>Is this you? Claim this profile — free</Link><p>Your profile is already public. Reviews are coming. Claim it now so your side is on the record first — and invite the clients who love you before anyone else weighs in.</p></div>
          )}
        </div>
        <ShareActions entity={p.entity} />
      </header>
      <nav className="ot-profile-nav">
        <a href="#record">The Record</a>
        <a href="#transparency">Transparency</a>
        <a href="#reviews">Reviews</a>
        <a href="#provenance">Provenance</a>
      </nav>
      <section id="record" className="ot-panel">
        <h2>The Record</h2>
        <p>
          Research the person and practice in context: what they publicly teach,
          the traditions and methods they describe, where they publish, their
          body of work, and how long the public record extends. For
          manifestation, law of attraction, law of assumption, tarot, psychic,
          mediumship, astrology, energy, and spiritual work alike, Observer
          Theory reports sources rather than deciding whether a belief or
          claimed outcome is true.
        </p>
        {Object.keys(grouped).length === 0 && <p>Not publicly available.</p>}
        {Object.entries(grouped).map(([field, values]) => (
          <div className="ot-fact" key={field}>
            <h3>{field.replace(/_/g, " ")}</h3>
            {values.map((a) => (
              <div key={a.id}>
                <span>{String(a.value)}</span>
                <small>
                  {typeLabel(a)} · Observed {date(a.observedAt)} ·{" "}
                  <a
                    href={"#source-" + a.sourceId}
                    onClick={() => observerApi.event("source_click")}
                  >
                    Source
                  </a>
                </small>
                {a.disputed && (
                  <mark>
                    A factual dispute about this section is under review.
                  </mark>
                )}
                <Link
                  to={`/report?kind=correction&entity=${p.entity.id}&assertion=${a.id}`}
                >
                  Suggest a correction to this fact
                </Link>
              </div>
            ))}
          </div>
        ))}
        <h3>Programs, readings, courses, and services</h3>
        <p>
          Commercial information appears separately from teachings and public
          content so that a person’s body of work is not reduced to what they
          sell.
        </p>
        {p.offers.length === 0 && <p>Not publicly available.</p>}
        {p.offers.map((o) => (
          <article className="ot-offer" key={o.id}>
            <h4>{o.name}</h4>
            {o.priceMinor ? (
              <p>
                {new Intl.NumberFormat("en-GB", {
                  style: "currency",
                  currency: o.currency || "GBP",
                }).format(o.priceMinor / 100)}
                {o.billingPeriod === "month" ? " / month" : ""}
              </p>
            ) : (
              <p>
                Price not publicly listed. This offer requires an application or
                enquiry to find out the cost.
              </p>
            )}
            {o.totalCommitmentMinor ? (
              <b>
                Total commitment:{" "}
                {new Intl.NumberFormat("en-GB", {
                  style: "currency",
                  currency: o.currency || "GBP",
                }).format(o.totalCommitmentMinor / 100)}
              </b>
            ) : null}
            <small>
              {new Date(o.staleAfter) < new Date()
                ? `Last checked ${date(o.observedAt)}. This may have changed.`
                : `Observed ${date(o.observedAt)}`}
            </small>
            {o.bookingUrl && (
              <a
                href={o.bookingUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => observerApi.event("booking_link_click")}
              >
                Official program, reading, course, or service page
              </a>
            )}
          </article>
        ))}
      </section>
      <section id="transparency" className="ot-panel ot-transparency">
        <h2>Transparency</h2>
        <div className="ot-fixed-explainer">
          Observer Theory records neutral, dated observations from approved public sources. A visibility change means only that something was visible on one observation date and no longer visible on another. It is not an accusation, a score, or an inference about why anything changed. Transparency observations never affect ranking, reviews, moderation, or visibility.
        </div>
        {p.transparencySummary.checked === 0 ? <p>No live YouTube comment-state observations are displayed. Collection remains policy-gated; this does not imply comments are enabled or disabled.</p> : <>
          <p>{p.transparencySummary.checked} recent videos checked; {p.transparencySummary.disabled} had comments unavailable at the dated observation. Observed {p.transparencySummary.observedAt ? date(p.transparencySummary.observedAt) : "date unavailable"}.</p>
          <ul>{p.transparency.map((observation) => <li key={observation.id}>{observation.videoId}: comments {observation.commentsEnabled ? "available" : "unavailable"} · observed {date(observation.observedAt)}</li>)}</ul>
          {p.visibilityChanges.map((change) => <p key={`${change.videoId}-${change.toDate}`}>{change.missingCount} comment identifiers visible on {date(change.fromDate)} were no longer visible on {date(change.toDate)}. No reason is inferred.</p>)}
        </>}
        {p.assertions.filter((assertion) => assertion.field === "transparency_response").map((response) => <aside key={response.id}><b>Practitioner response</b><p>{String(response.value)}</p><small>Observed {date(response.observedAt)}</small></aside>)}
        <Link to={`/report?kind=transparency_response&entity=${p.entity.id}`}>Add a practitioner response — free</Link>
      </section>
      <section id="reviews" className="ot-panel">
        <h2>Reviews</h2>
        {p.verifiedReviewCount < 5 && (
          <div className="ot-empty-inline">{COPY.zero}</div>
        )}
        {p.dimensionScores && (
          <>
            <div className="ot-dimensions">
              {REVIEW_DIMENSIONS.filter(
                ({ key }) => p.dimensionScores?.[key] != null,
              ).map(({ key, label }) => (
                <div key={key}>
                  <b>{label}</b>
                  <span>{p.dimensionScores![key].toFixed(1)} / 5</span>
                </div>
              ))}
            </div>
            {p.dimensionScores.audienceConduct != null && (
              <p className="ot-rating-note">
                Audience / community conduct reflects verified purchasers’
                firsthand observations of audience behaviour. It does not
                attribute that behaviour to the practitioner and never affects
                organic ranking.
              </p>
            )}
          </>
        )}
        <Link
          className="button button-ink"
          to={
            "/write-review?entity=" +
            p.entity.id +
            "&name=" +
            encodeURIComponent(p.entity.displayName)
          }
        >
          Write a review
        </Link>
        <h3>Verified purchase</h3>
        {verified.map((r) => (
          <ReviewCard key={r.id} r={r} entityId={p.entity.id} />
        ))}
        <h3>Client — purchase not verified.</h3>
        {unverified.map((r) => (
          <ReviewCard key={r.id} r={r} entityId={p.entity.id} />
        ))}
        <h3>Opinions on free content. These people did not buy anything.</h3>
        {opinions.map((r) => (
          <ReviewCard key={r.id} r={r} entityId={p.entity.id} />
        ))}
        <p>
          {p.removalCount === 1
            ? "1 review on this profile has been removed for policy violations."
            : `${p.removalCount} reviews on this profile have been removed for policy violations.`}
        </p>
      </section>
      <section id="provenance" className="ot-panel">
        <h2>Provenance</h2>
        <p>
          Every visible record statement above is typed, sourced, and dated.
        </p>
        {p.sources.map((s) => (
          <div id={"source-" + s.id} key={s.id}>
            {s.previewImage && <img className="ot-source-image" src={s.previewImage.url} alt="" />}
            <a
              href={s.url}
              target="_blank"
              rel="noreferrer"
              onClick={() => observerApi.event("source_click")}
            >
              {s.title || s.url}
            </a>{" "}
            · observed {date(s.observedAt)}
          </div>
        ))}
        <Link to={"/report?kind=correction&entity=" + p.entity.id}>
          See something wrong? Suggest a correction — no account or payment
          needed.
        </Link>
      </section>
    </div>
  );
}
